/**
 * Inventory Business Logic Hooks
 *
 * TanStack Query hooks for inventory management
 * Optimized for caching with shorter refresh intervals since inventory changes frequently
 */

import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { getInventoryListOptimizedApi, getInventoryBatchDetailApi, updateInventoryApi } from './inventory.api';

// Query Keys for business inventory domain
export const businessInventoryKeys = {
    all: ['business', 'inventory'],
    inventoryList: (params) => [...businessInventoryKeys.all, 'list', params],
    inventoryBatch: (batchNumber) => [...businessInventoryKeys.all, 'batch', batchNumber],
};

/**
 * Hook to get infinite inventory list with search and pagination
 *
 * Uses infinite query for optimized loading and caching:
 * - Infinite scroll pagination
 * - Smart search debouncing
 * - Optimized caching with search parameters
 * - Background refetch for better UX
 * - Automatic cache invalidation on inventory changes
 *
 * @param {object} params - Query parameters
 * @param {string} params.inventoryType - Type of inventory (managed/unmanaged)
 * @param {number} params.limit - Number of items per page (default: 25)
 * @param {string} params.search - Search term
 * @param {string} params.sortBy - Sort field
 * @param {string} params.sortType - Sort order (ASC/DESC)
 * @param {array} params.partsCategory - Array of parts category filters
 * @param {array} params.partsBrand - Array of parts brand filters
 * @param {array} params.supplier - Array of supplier filters
 * @param {array} params.tyreDimension - Array of tyre dimension filters
 * @param {array} params.tyreModel - Array of tyre model filters
 * @param {object} options - Query options
 * @returns {object} Infinite query result with inventory data
 */
export const useInfiniteInventoryList = (params = {}, options = {}) => {
    // Set default limit if not provided
    const queryParams = {
        pagination: true,
        limit: 25,
        ...params,
    };

    return useInfiniteQuery({
        queryKey: businessInventoryKeys.inventoryList(queryParams),
        queryFn: ({ pageParam = 1 }) => {
            return getInventoryListOptimizedApi({
                ...queryParams,
                page: pageParam,
            });
        },
        getNextPageParam: (lastPage, allPages) => {
            // Check if there are more pages
            if (lastPage?.totalPages && allPages.length < lastPage.totalPages) {
                return allPages.length + 1;
            }
            return undefined;
        },
        getPreviousPageParam: (firstPage, allPages) => {
            // For previous page support (optional)
            if (allPages.length > 1) {
                return allPages.length - 1;
            }
            return undefined;
        },
        staleTime: 2 * 60 * 1000, // 2 minutes - shorter than other queries since inventory changes frequently
        cacheTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false, // Prevent refetch on window focus for better performance
        keepPreviousData: true, // Keep previous data while fetching new data
        ...options,
    });
};

/**
 * Hook to get inventory batch details
 *
 * @param {string} batchNumber - Batch number to get details for
 * @param {object} options - Query options
 * @returns {object} Query result with batch details
 */
export const useGetInventoryBatchDetail = (batchNumber, options = {}) => {
    return useQuery({
        queryKey: businessInventoryKeys.inventoryBatch(batchNumber),
        queryFn: () => getInventoryBatchDetailApi(batchNumber),
        enabled: !!batchNumber, // Only run query if batchNumber is provided
        ...options,
    });
};

/**
 * Hook to update inventory item
 *
 * Automatically invalidates and refetches inventory lists after update
 * Provides optimistic updates for better UX
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result with update function
 */
export const useUpdateInventory = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ inventoryId, batchNumber, payload }) => updateInventoryApi(inventoryId, batchNumber, payload),
        onSuccess: (data, variables) => {
            // Invalidate all inventory queries to ensure fresh data
            queryClient.invalidateQueries({ queryKey: businessInventoryKeys.all });

            // Also invalidate specific batch query if batchNumber was provided
            if (variables.batchNumber) {
                queryClient.invalidateQueries({
                    queryKey: businessInventoryKeys.inventoryBatch(variables.batchNumber),
                });
            }
        },
        onError: (error) => {
            console.error('Error updating inventory:', error);
        },
        ...options,
    });
};

/**
 * Hook to refetch inventory list (for use after inventory creation/updates)
 *
 * This hook provides a function to manually refetch inventory data
 * Useful after creating new inventory items or making changes that require fresh data
 *
 * @param {object} params - Current query parameters to refetch
 * @returns {function} Refetch function
 */
export const useRefetchInventoryList = (params = {}) => {
    const queryClient = useQueryClient();

    return () => {
        // Invalidate and refetch all inventory queries
        queryClient.invalidateQueries({ queryKey: businessInventoryKeys.all });

        // If specific params are provided, refetch that specific query
        if (Object.keys(params).length > 0) {
            queryClient.refetchQueries({
                queryKey: businessInventoryKeys.inventoryList(params),
                type: 'active', // Only refetch if the query is currently active
            });
        }
    };
};
